import { makeAutoObservable } from "mobx";

const CANVAS_PANE_WIDTH_DEFAULT = 320;
const CANVAS_PANE_WIDTH_MIN = 200;
const CANVAS_PANE_WIDTH_MAX = 720;

const SIDEBAR_WIDTH_DEFAULT = 320;
const SIDEBAR_WIDTH_MIN = 240;
const SIDEBAR_WIDTH_MAX = 560;

export type AnalyticsToolbarMode = "chart" | "form" | "json" | "table";

/** Chart workspace sub-mode: rendered chart vs editable JSON vs config form for `more.charts[i]`. */
export type ChartPaneMode = "view" | "json" | "config";

export interface AnalyticsFacet {
  id: string;
  facet_id?: number;
  name: string;
  jsonContent?: string;
  etl_json?: Record<string, unknown>;
  facet_result?: Record<string, unknown>;
  version?: number;
  [key: string]: unknown;
}

export class TarsAnalyticsStore {
  rootStore: unknown;
  selectedSourceIds: string[] = [];
  projectQuestionKeys: string[] = [];
  projectQuestionKeysLoading = false;
  projectQuestionKeysError: string | null = null;
  facets: AnalyticsFacet[] = [];
  analyticsTitle = "Analytics 1";
  canvasPaneWidth = CANVAS_PANE_WIDTH_DEFAULT;
  sidebarWidth = SIDEBAR_WIDTH_DEFAULT;
  selectedFacetId: string | null = null;
  analyticsToolbarMode: AnalyticsToolbarMode = "json";
  chartPane: ChartPaneMode = "view";
  chartDraft = "";

  constructor(rootStore: unknown) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  setSelectedSourceIds(ids: string[]) {
    this.selectedSourceIds = ids;
  }

  setProjectQuestionKeys(keys: string[]) {
    this.projectQuestionKeys = keys;
  }

  setProjectQuestionKeysLoading(loading: boolean) {
    this.projectQuestionKeysLoading = loading;
  }

  setProjectQuestionKeysError(message: string | null) {
    this.projectQuestionKeysError = message;
  }
  
  toggleSourceSelection(id: string) {
    const idx = this.selectedSourceIds.indexOf(id);
    if (idx === -1) this.selectedSourceIds = [...this.selectedSourceIds, id];
    else this.selectedSourceIds = this.selectedSourceIds.filter((x) => x !== id);
  }

  setFacets(facets: AnalyticsFacet[]) {
    this.facets = facets;
  }

  addFacet(facet: Omit<AnalyticsFacet, "id">) {
    const id = `facet-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.facets = [...this.facets, { ...facet, id } as AnalyticsFacet];
    this.selectedFacetId = id;
  }

  removeFacet(id: string) {
    const wasSelected = this.selectedFacetId === id;
    this.facets = this.facets.filter((f) => f.id !== id);
    if (wasSelected) {
      this.selectedFacetId = this.facets[0]?.id ?? null;
    }
  }

  updateFacet(id: string, updates: Partial<AnalyticsFacet>) {
    const facetIndex = this.facets.findIndex((f) => f.id === id);
    if (facetIndex !== -1) {
      this.facets = [
        ...this.facets.slice(0, facetIndex),
        { ...this.facets[facetIndex], ...updates },
        ...this.facets.slice(facetIndex + 1),
      ];
    }
  }

  setSelectedFacetId(id: string | null) {
    this.selectedFacetId = id;
  }

  setAnalyticsToolbarMode(mode: AnalyticsToolbarMode) {
    this.analyticsToolbarMode = mode;
  }

  setChartPane(mode: ChartPaneMode) {
    this.chartPane = mode;
  }

  setChartDraft(text: string) {
    this.chartDraft = text;
  }

  syncChartDraft(jsonString: string) {
    this.chartDraft = jsonString;
  }

  get facetCount(): number {
    return this.facets.length;
  }

  setAnalyticsTitle(title: string) {
    this.analyticsTitle = title;
  }

  setCanvasPaneWidth(width: number) {
    const clamped = Math.max(
      CANVAS_PANE_WIDTH_MIN,
      Math.min(CANVAS_PANE_WIDTH_MAX, width)
    );
    this.canvasPaneWidth = clamped;
  }

  setSidebarWidth(width: number) {
    const clamped = Math.max(
      SIDEBAR_WIDTH_MIN,
      Math.min(SIDEBAR_WIDTH_MAX, width)
    );
    this.sidebarWidth = clamped;
  }
}
